/**
 * Utilitaire pour gérer les permissions des utilisateurs sur les classes
 *
 * Matrice des permissions:
 * ┌─────────────────────────────┬───────────────┬──────────────────┐
 * │ Action                      │ Accès Complet │ Accès Restreint  │
 * ├─────────────────────────────┼───────────────┼──────────────────┤
 * │ Import élèves               │      ✅       │        ✅        │
 * │ Ajout/Modif élève           │      ✅       │        ✅        │
 * │ Suppression élève (sans)    │      ✅       │        ✅        │
 * │ Suppression élève (avec)    │      ✅       │        ❌        │
 * │ Import Eval Nat             │      ✅       │        ✅        │
 * │ Créer évaluation            │      ✅       │        ❌        │
 * │ Modifier résultats          │      ✅       │        ❌        │
 * │ Voir résultats              │      ✅       │        ❌        │
 * │ Voir savoir-faire           │      ✅       │        ❌        │
 * └─────────────────────────────┴───────────────┴──────────────────┘
 */

/**
 * Vérifie si l'utilisateur a un accès complet à la classe
 * (c'est-à-dire que c'est une de ses classes attribuées)
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const hasFullAccess = (classeId, user, etablissementData) => {
  if (!user || !etablissementData || !classeId) return false;

  // Le chef a toujours un accès complet
  if (user.role === 'chef') return true;

  // Vérifier si c'est une classe ou un groupe attribué au professeur
  if (user.role === 'professeur') {
    const professeur = etablissementData.professeurs?.find(prof => prof.id === user.id);
    // Vérifier dans les classes ET dans les groupes
    const hasClasseAccess = professeur?.classes?.includes(classeId) || false;
    const hasGroupeAccess = professeur?.groupes?.includes(classeId) || false;
    return hasClasseAccess || hasGroupeAccess;
  }

  return false;
};

/**
 * Vérifie si l'utilisateur a un accès restreint à la classe
 * (délégué d'affectation pour une classe de l'équipe qui n'est pas attribuée)
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const hasRestrictedAccess = (classeId, user, etablissementData) => {
  if (!user || !etablissementData || !classeId || user.role !== 'professeur') return false;

  const professeur = etablissementData.professeurs?.find(prof => prof.id === user.id);

  // Pas délégué d'affectation = pas d'accès restreint
  if (!professeur?.delegue_affectation) return false;

  // Si c'est une classe ou groupe attribué, ce n'est pas un accès restreint (c'est un accès complet)
  if (professeur.classes?.includes(classeId) || professeur.groupes?.includes(classeId)) return false;

  // Vérifier si c'est une classe ou un groupe de l'établissement
  const classe = etablissementData.classes?.find(c => c.id === classeId);
  const groupe = etablissementData.groupes?.find(g => g.id === classeId);
  if (!classe && !groupe) return false;

  // Trouver les profs qui enseignent cette classe/groupe
  const profsDeClasse = etablissementData.professeurs?.filter(p =>
    (p.classes && p.classes.includes(classeId)) || (p.groupes && p.groupes.includes(classeId))
  );

  // Vérifier si au moins un prof de cette classe/groupe enseigne la même matière
  return profsDeClasse?.some(p =>
    p.matiere && p.matiere.toLowerCase() === professeur.matiere?.toLowerCase()
  ) || false;
};

/**
 * Retourne le type d'accès de l'utilisateur à la classe
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {'full' | 'restricted' | 'none'}
 */
export const getAccessType = (classeId, user, etablissementData) => {
  if (hasFullAccess(classeId, user, etablissementData)) return 'full';
  if (hasRestrictedAccess(classeId, user, etablissementData)) return 'restricted';
  return 'none';
};

/**
 * Vérifie si l'utilisateur peut gérer les élèves (ajouter/modifier)
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canManageStudents = (classeId, user, etablissementData) => {
  // Accès complet ou restreint permettent de gérer les élèves
  return hasFullAccess(classeId, user, etablissementData) ||
         hasRestrictedAccess(classeId, user, etablissementData);
};

/**
 * Vérifie si l'utilisateur peut importer des élèves
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canImportStudents = (classeId, user, etablissementData) => {
  // Accès complet ou restreint permettent d'importer des élèves
  return canManageStudents(classeId, user, etablissementData);
};

/**
 * Vérifie si l'utilisateur peut supprimer un élève spécifique
 * En accès restreint, on ne peut supprimer que les élèves sans résultats
 * @param {number} classeId - ID de la classe
 * @param {object} eleve - Objet élève (doit avoir la propriété resultats qui est un tableau)
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canDeleteStudent = (classeId, eleve, user, etablissementData) => {
  // Accès complet = peut supprimer tous les élèves
  if (hasFullAccess(classeId, user, etablissementData)) {
    return true;
  }

  // Accès restreint = peut supprimer uniquement les élèves sans résultats
  if (hasRestrictedAccess(classeId, user, etablissementData)) {
    // Vérifier si l'élève a des résultats
    // Un élève sans résultats a un tableau resultats vide ou undefined
    const hasResults = eleve?.resultats && eleve.resultats.length > 0;
    return !hasResults;
  }

  return false;
};

/**
 * Vérifie si l'utilisateur peut créer/modifier/supprimer des évaluations
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canManageEvaluations = (classeId, user, etablissementData) => {
  // Seul l'accès complet permet de gérer les évaluations
  return hasFullAccess(classeId, user, etablissementData);
};

/**
 * Vérifie si l'utilisateur peut modifier des résultats
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canEditResults = (classeId, user, etablissementData) => {
  // Seul l'accès complet permet de modifier les résultats
  return hasFullAccess(classeId, user, etablissementData);
};

/**
 * Vérifie si l'utilisateur peut consulter les résultats
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canViewResults = (classeId, user, etablissementData) => {
  // Seul l'accès complet permet de voir les résultats
  return hasFullAccess(classeId, user, etablissementData);
};

/**
 * Vérifie si l'utilisateur peut consulter les savoir-faire
 * @param {number} classeId - ID de la classe
 * @param {object} user - Objet utilisateur
 * @param {object} etablissementData - Données de l'établissement
 * @returns {boolean}
 */
export const canViewSavoirFaire = (classeId, user, etablissementData) => {
  // Seul l'accès complet permet de voir les savoir-faire
  return hasFullAccess(classeId, user, etablissementData);
};
